'use client'

import { cx } from 'cva'
import Image from 'next/image'
import type { FC } from 'react'
import { useRef } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { EffectFade, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { ImageData } from '../../frontend-types'
import { IconCircle } from '../atoms/IconCircle'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

export type CarouselCardImagesProps = {
	images: ImageData[]
	imageAspectRatio?: 'card-feature' | 'video'
	imagesSizes: string
}

export const bulletClass = 'bg-theme-fg opacity-100 w-2 h-2 block rounded-full cursor-pointer'
export const bulletActiveClass = 'border-(length:--stroke-sm) border-white !bg-blue'

export const CarouselCardImages: FC<CarouselCardImagesProps> = ({
	images,
	imageAspectRatio = 'video',
	imagesSizes,
}) => {
	const swiperRef = useRef<SwiperType | null>(null)

	if (!images || images.length === 0) {
		return null
	}

	const handlePrev = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		if (swiperRef.current) {
			swiperRef.current.slidePrev()
		}
	}

	const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		if (swiperRef.current) {
			swiperRef.current.slideNext()
		}
	}

	return (
		<div className="group relative h-full w-full overflow-hidden rounded-tl-sm tablet-p:rounded-tl-none">
			<Swiper
				effect="fade"
				onSwiper={(swiper) => {
					swiperRef.current = swiper
				}}
				modules={[Pagination, EffectFade]}
				speed={1000}
				spaceBetween={0}
				slidesPerView={1}
				navigation={false}
				loop={images.length > 1}
				pagination={{
					clickable: true,
					bulletClass,
					bulletActiveClass,
				}}
				className="h-full w-full [&_.swiper-pagination]:!bottom-xl [&_.swiper-pagination]:!flex [&_.swiper-pagination]:!items-center [&_.swiper-pagination]:!justify-center [&_.swiper-pagination]:!gap-2"
			>
				{images.map((image) => (
					<SwiperSlide key={image.src}>
						<div
							className={cx(
								'relative h-full w-full',
								imageAspectRatio === 'card-feature' ? 'aspect-card-feature' : 'aspect-video',
							)}
						>
							<Image
								src={image.src}
								alt={image.alt}
								fill
								className="object-cover"
								sizes={imagesSizes}
							/>
						</div>
					</SwiperSlide>
				))}
			</Swiper>

			{/* Custom Navigation */}
			{images.length > 1 && (
				<div className="pointer-events-none hidden opacity-30 transition-opacity duration-200 ease-in-out group-hover:pointer-events-auto group-hover:opacity-100 tablet-l:block">
					<button
						onClick={handlePrev}
						className="absolute top-1/2 left-xl z-10 -translate-y-1/2 cursor-pointer group"
						aria-label="Previous image"
					>
						<IconCircle icon={['fas', 'chevron-left']} size="sm" />
					</button>
					<button
						onClick={handleNext}
						className="absolute top-1/2 right-xl z-10 -translate-y-1/2 cursor-pointer group"
						aria-label="Next image"
					>
						<IconCircle icon={['fas', 'chevron-right']} size="sm" />
					</button>
				</div>
			)}
		</div>
	)
}
